import { BadgeCheck, Pencil, Plus, Trash2, Undo2, Wallet } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Money } from '@/components/ui/Money'
import { ApproveIncomingInvoiceModal } from './ApproveIncomingInvoiceModal'
import { SearchSelect } from '@/components/ui/SearchSelect'
import { useAuth, useIsAdmin } from '@/lib/auth'
import { parseAmountInput, toDecimal, toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { useClearDistribution, useSetDistribution, useSetPayout } from '@/lib/invoice-queries'
import { useBeneficiaries } from '@/lib/recurring-api'
import type { Invoice } from '@/lib/invoice-types'
import { todayIsoDate } from '@/lib/week'
import { useApproveIncomingInvoice } from '@/lib/payout-queries'
import { cn } from '@/lib/utils'
import { waitingLineOf } from '@/lib/payout-waiting'
import { IncomingInvoiceControls } from './IncomingInvoiceControls'
import { PayoutWaitingLabel } from './PayoutWaitingLabel'

const CAN_PAYOUT: Invoice['status'][] = ['APPROVED', 'SENT', 'PAID']

export function DistributionTab({ invoice }: { invoice: Invoice }) {
  const [payoutLineId, setPayoutLineId] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const isAdmin = useIsAdmin()

  const canPayout = CAN_PAYOUT.includes(invoice.status)
  // Zodra een uitbetaling onderweg is weigert de server de wijziging; dan is de
  // knop aanbieden en pas bij opslaan struikelen de verkeerde volgorde.
  const payoutsOpen = invoice.distribution.every((line) => line.payoutStage === 'OPEN')
  const canEdit = isAdmin && invoice.status === 'DRAFT' && payoutsOpen

  if (editing) {
    return <DistributionEditor invoice={invoice} onClose={() => setEditing(false)} />
  }

  if (invoice.distribution.length === 0) {
    return (
      <div className="space-y-3">
        <p className="py-8 text-center text-sm text-ink-faint">Deze factuur heeft geen verdeling.</p>
        {canEdit ? <EditBar invoice={invoice} onEdit={() => setEditing(true)} /> : null}
      </div>
    )
  }

  const distributionTotal = invoice.distribution.reduce((sum, d) => sum + toNumber(d.amount), 0)

  return (
    <div className="space-y-3">
      {invoice.manualDistribution ? (
        <div className="rounded-card border border-hairline bg-fill-1 px-4 py-3 text-sm">
          <p className="label-caps text-ink-faint">Handmatig verdeeld</p>
          <p className="mt-1 text-ink-soft">{invoice.manualDistribution.reason}</p>
        </div>
      ) : null}
      {invoice.distribution.map((line) => (
        <div key={line.id} className="rounded-card border border-hairline p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-ink">{line.beneficiaryLabel}</span>
                {line.isManualOverride ? (
                  <span className="rounded-pill bg-status-approval/10 px-2 py-0.5 text-xs font-medium text-status-approval">
                    Handmatig
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-sm text-ink-soft">{line.explanation}</p>
            </div>
            <div className="text-right">
              <Money
                amount={toNumber(line.amount)}
                secondaryAmount={line.incomingInvoiceTotal ? toNumber(line.incomingInvoiceTotal) : null}
                className="font-semibold text-ink"
              />
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3 border-t border-hairline pt-3">
            <PayoutWaitingLabel line={waitingLineOf(invoice, line)} />
            <div className="flex items-center gap-1.5">
              {canPayout ? (
                <button type="button" className="btn-ghost h-8 px-2 text-sm" onClick={() => setPayoutLineId(line.id)}>
                  <Wallet className="h-3.5 w-3.5" />
                  Uitbetaling
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ))}

      <div className="flex items-center justify-between rounded-card bg-fill-1 px-4 py-3 text-sm">
        <span className="text-ink-soft">Totaal verdeeld</span>
        <span className="money font-semibold text-ink">{formatEuros(distributionTotal)}</span>
      </div>

      {canEdit ? <EditBar invoice={invoice} onEdit={() => setEditing(true)} /> : null}

      {payoutLineId ? <PayoutDialog invoice={invoice} lineId={payoutLineId} onClose={() => setPayoutLineId(null)} /> : null}
    </div>
  )
}

function EditBar({ invoice, onEdit }: { invoice: Invoice; onEdit: () => void }) {
  const clear = useClearDistribution(invoice.id)

  return (
    <div className="flex items-center justify-end gap-2">
      {invoice.manualDistribution ? (
        <button type="button" className="btn-ghost text-sm" onClick={() => clear.mutate()} disabled={clear.isPending}>
          <Undo2 className="h-3.5 w-3.5" />
          Terug naar het klant-script
        </button>
      ) : null}
      <button type="button" className="btn-ghost text-sm" onClick={onEdit}>
        <Pencil className="h-3.5 w-3.5" />
        Verdeling aanpassen
      </button>
    </div>
  )
}

type EditRow = { key: number; beneficiary: string; amount: string }

let rowKey = 0

/**
 * Wat de knop toetst moet exact zijn wat er wordt verstuurd: elk bedrag eerst
 * afronden op centen, dan pas optellen. Op de onafgeronde invoer sluit een
 * verdeling soms wel terwijl de server hem daarna weigert.
 */
const roundedAmounts = (rows: EditRow[]): (number | null)[] =>
  rows.map((row) => {
    const parsed = parseAmountInput(row.amount)
    return parsed === null ? null : Number(parsed.toFixed(2))
  })

function toEditRows(invoice: Invoice): EditRow[] {
  return invoice.distribution.map((line) => ({
    key: rowKey++,
    beneficiary: line.personId ?? line.externalPartyId ?? '',
    amount: line.amount,
  }))
}

/**
 * De verdeling van een concept handmatig zetten. Wat je hier invult vervangt de
 * uitkomst van het klant-script voor deze ene factuur; opslaan kan pas als de som
 * exact op het subtotaal sluit, want dat is ook de eis van de backend.
 */
function DistributionEditor({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const subtotal = toNumber(invoice.subtotal)
  const { data: beneficiaries = [] } = useBeneficiaries()
  const setDistribution = useSetDistribution(invoice.id)

  // De keuzelijst toont alleen actieve ontvangers, maar een bestaande verdeelregel
  // kan naar iemand wijzen die inmiddels inactief is. Die hoort er gewoon bij te
  // staan, anders leest zijn rij als leeg terwijl er wel een bedrag op staat.
  const beneficiaryOptions = useMemo(() => {
    const options = beneficiaries.map((b) => ({ value: b.id, label: b.label }))
    const known = new Set(options.map((o) => o.value))
    for (const line of invoice.distribution) {
      const id = line.personId ?? line.externalPartyId
      if (id && !known.has(id)) {
        options.push({ value: id, label: line.beneficiaryLabel })
        known.add(id)
      }
    }
    return options
  }, [beneficiaries, invoice.distribution])

  const [rows, setRows] = useState<EditRow[]>(() => toEditRows(invoice))
  const [reason, setReason] = useState(invoice.manualDistribution?.reason ?? '')

  const signature = useMemo(
    () => invoice.distribution.map((d) => `${d.id}:${d.amount}`).join('|'),
    [invoice.distribution],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: alleen resetten als de verdeling zelf wijzigt, niet bij elke nieuwe invoice-referentie
  useEffect(() => {
    setRows(toEditRows(invoice))
  }, [signature])

  const amounts = roundedAmounts(rows)
  const readable = amounts.every((amount) => amount !== null)
  const sum = amounts.reduce<number>((total, amount) => total + (amount ?? 0), 0)
  const remaining = subtotal - sum
  const closes = readable && Math.abs(remaining) < 0.005
  const complete = rows.length > 0 && rows.every((row) => row.beneficiary !== '')
  const unique = new Set(rows.map((row) => row.beneficiary)).size === rows.length
  const canSave = closes && complete && unique && reason.trim() !== ''

  function save() {
    setDistribution.mutate(
      {
        entries: rows.map((row, index) => ({ beneficiary: row.beneficiary, amount: toDecimal(amounts[index] ?? 0) })),
        reason: reason.trim(),
      },
      { onSuccess: onClose },
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-soft">
        Deze bedragen vervangen de uitkomst van het klant-script voor alleen deze factuur. Ze blijven staan bij
        herberekenen en bij het bewerken van de regels.
      </p>

      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={row.key} className="flex items-center gap-2">
            <SearchSelect
              value={row.beneficiary}
              onValueChange={(value) =>
                setRows((prev) => prev.map((r, i) => (i === index ? { ...r, beneficiary: value } : r)))
              }
              options={beneficiaryOptions}
              placeholder="Ontvanger"
              className="flex-1"
            />
            <input
              value={row.amount}
              onChange={(e) => setRows((prev) => prev.map((r, i) => (i === index ? { ...r, amount: e.target.value } : r)))}
              inputMode="decimal"
              placeholder="bedrag"
              aria-label="Bedrag"
              className="field num w-32 text-right"
            />
            <button
              type="button"
              className="btn-ghost h-8 w-8 p-0 text-ink-faint"
              onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
              aria-label="Ontvanger verwijderen"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}

        <button
          type="button"
          className="btn-ghost text-sm"
          onClick={() => setRows((prev) => [...prev, { key: rowKey++, beneficiary: '', amount: '' }])}
        >
          <Plus className="h-4 w-4" />
          Ontvanger
        </button>
      </div>

      <div className="flex items-center justify-between rounded-card bg-fill-1 px-4 py-3 text-sm">
        <span className="text-ink-soft">{closes ? 'Sluit op het subtotaal' : 'Nog te verdelen'}</span>
        <span className={cn('money font-semibold', closes ? 'text-ink' : 'text-status-overdue')}>
          {!readable ? 'onleesbaar bedrag' : closes ? formatEuros(subtotal) : formatEuros(remaining)}
        </span>
      </div>

      <label className="block">
        <span className="label-caps text-ink-faint">Reden</span>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Waarom wijkt deze factuur af van het script?"
          className="field mt-1 w-full"
        />
      </label>

      {!unique ? <p className="text-xs text-status-overdue">Elke ontvanger mag maar een keer voorkomen.</p> : null}

      <div className="flex items-center justify-end gap-2">
        <button type="button" className="btn-ghost" onClick={onClose}>
          Annuleren
        </button>
        <button type="button" className="btn-primary" onClick={save} disabled={!canSave || setDistribution.isPending}>
          {setDistribution.isPending ? 'Opslaan…' : 'Verdeling opslaan'}
        </button>
      </div>
    </div>
  )
}

function PayoutDialog({ invoice, lineId, onClose }: { invoice: Invoice; lineId: string; onClose: () => void }) {
  const line = useMemo(() => invoice.distribution.find((d) => d.id === lineId) ?? null, [invoice.distribution, lineId])
  const setPayout = useSetPayout(invoice.id)
  const approve = useApproveIncomingInvoice()
  const { data: me } = useAuth()
  const isAdmin = me?.user.role === 'ADMIN'

  const [paid, setPaid] = useState(line?.payoutStatus === 'PAID')
  const [paidAt, setPaidAt] = useState(line?.payoutPaidAt ?? todayIsoDate())
  const [incoming, setIncoming] = useState(line?.incomingInvoiceNumber ?? '')
  const [explaining, setExplaining] = useState(false)

  const difference = toNumber(line?.incomingInvoiceDifference ?? '0')

  function startApprove() {
    setExplaining(true)
  }

  if (!line) {
    return null
  }

  const approved = line.payoutStage === 'GEACCORDEERD' || line.payoutStage === 'BETAALD'
  const invoicePaid = invoice.status === 'PAID'
  const canMarkPaid = invoicePaid && (approved || line.autoSettle)

  function save() {
    if (!line) return
    setPayout.mutate(
      {
        lineId: line.id,
        input: {
          payoutStatus: paid ? 'PAID' : 'OPEN',
          payoutPaidAt: paid ? paidAt : null,
          incomingInvoiceNumber: incoming.trim() || null,
        },
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Uitbetaling · ${line.beneficiaryLabel}`}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            {isAdmin ? 'Annuleren' : 'Sluiten'}
          </button>
          {isAdmin ? (
            <button type="button" className="btn-primary" onClick={save} disabled={setPayout.isPending}>
              Opslaan
            </button>
          ) : null}
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <PayoutWaitingLabel line={waitingLineOf(invoice, line)} />
          <IncomingInvoiceControls
            lineId={line.id}
            stage={line.payoutStage}
            hasPdf={line.hasIncomingInvoicePdf}
            filename={line.incomingInvoiceFilename}
            incomingInvoiceNumber={line.incomingInvoiceNumber}
            share={line.amount}
            canManage={isAdmin}
            canView
            autoSettle={line.autoSettle}
          />
        </div>

        {explaining && (
          <ApproveIncomingInvoiceModal
            lineId={line.id}
            canViewPdf={isAdmin}
            share={line.amount}
            invoiceNumber={line.incomingInvoiceNumber}
            invoicedAmount={line.incomingInvoiceAmount}
            invoicedTotal={line.incomingInvoiceTotal}
            difference={difference}
            reason={line.incomingInvoiceDifferenceReason}
            pending={approve.isPending}
            onClose={() => setExplaining(false)}
            onConfirm={() => approve.mutate({ lineId: line.id, approve: true }, { onSuccess: () => setExplaining(false) })}
          />
        )}

        {isAdmin && !line.autoSettle && line.hasIncomingInvoicePdf && line.payoutStage !== 'BETAALD' ? (
          <div className="flex items-center gap-2">
            {line.payoutStage === 'GEACCORDEERD' ? (
              <button
                type="button"
                className="btn-ghost h-8 px-2 text-sm"
                onClick={() => approve.mutate({ lineId: line.id, approve: false })}
                disabled={approve.isPending}
              >
                <Undo2 className="h-3.5 w-3.5" />
                Accordering intrekken
              </button>
            ) : (
              <button
                type="button"
                className="btn-ghost h-8 px-2 text-sm"
                onClick={startApprove}
                disabled={approve.isPending}
              >
                <BadgeCheck className="h-3.5 w-3.5" />
                Inkoopfactuur accorderen
              </button>
            )}
          </div>
        ) : null}

        {isAdmin ? (
        <div className="space-y-3 border-t border-hairline pt-4">
          <div className="inline-flex items-center gap-0.5 rounded-lg border border-hairline bg-fill-2 p-0.5">
            {[
              { value: false, label: 'Open' },
              { value: true, label: 'Betaald' },
            ].map((option) => {
              const disabled = option.value === true && !canMarkPaid
              return (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => !disabled && setPaid(option.value)}
                  disabled={disabled}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    paid === option.value ? 'bg-surface text-ink shadow-card' : 'text-ink-soft',
                    disabled ? 'cursor-not-allowed opacity-50' : '',
                  )}
                >
                  {option.label}
                </button>
              )
            })}
          </div>
          {!canMarkPaid ? (
            <p className="text-xs text-ink-faint">
              {!invoicePaid
                ? 'Uitbetaling kan pas op betaald nadat de klant de factuur betaald heeft.'
                : 'Uitbetaling kan pas op betaald na accordering van de inkoopfactuur.'}
            </p>
          ) : null}
          {paid ? (
            <>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink-soft">Betaaldatum</span>
                <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="field" />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-ink-soft">Inkomend factuurnummer</span>
                <input value={incoming} onChange={(e) => setIncoming(e.target.value)} placeholder="Bijv. RS-2025-08" className="field" />
              </label>
            </>
          ) : null}
        </div>
        ) : null}
      </div>
    </Modal>
  )
}
